import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { RideRouteCompletionStatusEnum } from "@ryde/shared";

import { buildRideRouteCompletionStatusBadgePalette } from "../../../utils";
import { LiveMapStatusBadgeStyled } from "./live-map-status-badge.styled";

export interface IRideRouteCompletionStatusBadge {
  status?: RideRouteCompletionStatusEnum | null;
}

/**
 * Rides grid "Route Completion Status" badge (RYDE-8037). A ride with no stations at all carries no
 * status and renders "No Data" — distinct from `NotDone`, where a planned route exists but nothing
 * on it was reached.
 */
export const RideRouteCompletionStatusBadge: FC<
  IRideRouteCompletionStatusBadge
> = ({ status }) => {
  const { t } = useTranslation();

  // Grid rows without a value pass "" (not undefined) — any unknown/falsy value is "No Data".
  const resolvedStatus =
    status && Object.values(RideRouteCompletionStatusEnum).includes(status)
      ? status
      : undefined;

  const palette = useMemo(
    () => buildRideRouteCompletionStatusBadgePalette(resolvedStatus),
    [resolvedStatus],
  );

  return (
    <LiveMapStatusBadgeStyled
      $backgroundColor={palette.backgroundColor}
      $borderColor={palette.borderColor}
      $color={palette.color}
      $dataE2e="ride-route-completion-status-badge"
    >
      {t(`ride.routeCompletionStatus.${resolvedStatus ?? "NoData"}`)}
    </LiveMapStatusBadgeStyled>
  );
};
